<script>
import Vue from 'vue';
import {mapState, mapActions, mapGetters} from 'vuex';
import PaginationControl from '../PaginationControl/PaginationControl.vue';
import DropdownMenu from '../DropdownMenu/DropdownMenu.vue';
import BookingItem from '../BookingItem/BookingItem.vue';
import DropDownMenuMultiSelect from '../DropDownMenuMultiSelect/DropDownMenuMultiSelect.vue';
import Fake from '../Fake/Fake.vue';

export default Vue.extend({
    components: {
        Fake,
        PaginationControl,
        DropdownMenu,
        BookingItem,
        DropDownMenuMultiSelect
    },
    filters: {
        noReference: (ref) => {
            return (ref) || 'n/a'
        }
    },
    data() {
        return {
            tooltip: false,
        }
    },
    computed: {
        ...mapState({
            loading: state => state.booking.bookings.loading,
            noContent: state => state.booking.bookings.noContent,
            error: state => state.booking.bookings.error,
            page: state => state.booking.bookings.page,
            limit: state => state.booking.bookings.limit,
            limitList: state => state.booking.bookings.limitList,
            predicate: state => state.booking.bookings.predicate,
            defaultPredicate: state => state.booking.bookings.defaultPredicate,
            predicateList: state => state.booking.bookings.predicateList,
            hasFetched: state => state.booking.bookings.hasFetched,
            navigationMinimized: state => state.nav.minimized,
            order: state => state.booking.bookings.order,
            orderList: state => state.booking.bookings.orderList,
            searchword: state => state.booking.bookings.filters.searchword,
            csv: state => state.booking.bookings.csv,
            user: state => state.auth.user,
        }),
        ...mapGetters({
            filteredBookings: 'booking/filteredBookings',
            filteredBookingsPaginated: 'booking/filteredBookingsPaginated',
            filteredBookingsMaxPages: 'booking/filteredBookingsMaxPages',
            filteredBookingsCSVFile: 'booking/filteredBookingsCSVFile',
            filteredBookingsXLSFile: 'booking/filteredBookingsXLSFile'
        }),
    },
    methods: {
        ...mapActions({
            incrementBookingPagination: 'booking/incrementBookingPagination',
            decrementBookingPagination: 'booking/decrementBookingPagination',
            setPredicate: 'booking/setPredicate',
            selectMultiPredicate: 'booking/selectMultiPredicate',
            setMultiPredicate: 'booking/setMultiPredicate',
            unsetMultiPredicate: 'booking/unsetMultiPredicate',
            resetMultiPredicate: 'booking/resetMultiPredicate',
            setLimit: 'booking/setLimit',
            setOrder: 'booking/setOrder',
            search: 'booking/search',
            downloadFile: 'booking/downloadFile',
            downloadXLS: 'booking/downloadXLS'
        }),
        downloadFile(xls) {
            // If not XLS then download as csv
            let encodedURI = encodeURI(xls ? this.filteredBookingsXLSFile : this.filteredBookingsCSVFile);
            let link = document.createElement('a');
            link.setAttribute('href', encodedURI);
            link.setAttribute('download', xls ? 'BookingsList.xls' : 'BookingsList.csv');
            document.body.appendChild(link); // Required for FF

            link.click();
        }
    }
});
</script>
<template>
    <div class="booking-list">
        <div v-if="!loading"
             :key="'body'"
             class="booking-list__body">
            <div :class="{'booking-list__header--minimized': !navigationMinimized}"
                 class="booking-list__header">
                <div class="booking-list__heading">
                    {{ $tc('bookingList.heading', filteredBookings.length, {count: filteredBookings.length}) }}
                    <span class="booking-list__download" @click="downloadFile(false)">
                        {{ $t('bookingList.download.csv').toUpperCase() }}
                    </span>
                    <span class="booking-list__download"
                          @click="downloadFile(true)"
                          @mouseenter="tooltip = true"
                          @mouseleave="tooltip = false">
                        {{ $t('bookingList.download.xls').toUpperCase() }}
                        <transition name="fade" mode="out-in">
                            <span v-if="tooltip" class="booking-list__xls-tooltip">
                                {{ $t('bookingList.xslTooltip') }}
                            </span>
                        </transition>
                    </span>
                </div>
                <div class="booking-list__search">
                    <input :placeholder="$t('bookingList.placeholder')"
                           :title="$t('bookingList.title')"
                           type="text"
                           class="booking-list__search-field"
                           @keyup="search($event.target.value)">
                </div>
                <div class="booking-list__option booking-list__option--pagination">
                    <pagination-control :max-pages="filteredBookingsMaxPages"
                                        :current-page="(filteredBookingsMaxPages) === 0 ? 0 : page"
                                        @next="incrementBookingPagination"
                                        @prev="decrementBookingPagination"/>
                </div>
                <div class="booking-list__option">
                    <dropdown-menu
                        :label="$t('bookingList.order')"
                        :value="order"
                        :items="orderList"
                        :translation-prefix="'bookingList.orderFields'"
                        :searchable="false"
                        @input="setOrder($event)"/>
                </div>
                <div class="booking-list__option">
                    <drop-down-menu-multi-select
                        :label="$t('bookingList.orderBy')"
                        :value="predicate"
                        :items="predicateList"
                        :default-value="defaultPredicate"
                        :translation-prefix="'booking.tableFields'"
                        @select="setMultiPredicate($event)"
                        @deselect="unsetMultiPredicate($event)"
                        @reset="resetMultiPredicate"/>
                </div>
                <div class="booking-list__option">
                    <dropdown-menu
                        :label="$t('bookingList.perPage')"
                        :value="limit"
                        :items="limitList"
                        :searchable="false"
                        @input="setLimit($event)"/>
                </div>
            </div>
            <table id="booking-list-table" class="booking-list__table">
                <thead>
                    <tr>
                        <th :class="{'booking-list__table-header--selected' : predicate[0] === 'supplierRef'}"
                            class="booking-list__table-header"
                            @click="selectMultiPredicate('supplierRef')">
                            {{ $t('booking.tableFields.supplierRef') }}
                        </th>
                        <th :class="{'booking-list__table-header--selected' : predicate[0] === 'bookingRef'}"
                            class="booking-list__table-header"
                            @click="selectMultiPredicate('bookingRef')">
                            {{ $t('booking.tableFields.bookingRef') }}
                        </th>
                        <th :class="{'booking-list__table-header--selected' : predicate[0] === 'customerName'}"
                            class="booking-list__table-header"
                            @click="selectMultiPredicate('customerName')">
                            {{ $t('booking.tableFields.customerName') }}
                        </th>
                        <th :class="{'booking-list__table-header--selected' : predicate[0] === 'status'}"
                            class="booking-list__table-header"
                            @click="selectMultiPredicate('status')">
                            {{ $t('booking.tableFields.status') }}
                        </th>
                        <th :class="{'booking-list__table-header--selected' : predicate[0] === 'bookingDate'}"
                            class="booking-list__table-header"
                            @click="selectMultiPredicate('bookingDate')">
                            {{ $t('booking.tableFields.bookingDate') }}
                        </th>
                        <th :class="{'booking-list__table-header--selected' : predicate[0] === 'arrivalDate'}"
                            class="booking-list__table-header"
                            @click="selectMultiPredicate('arrivalDate')">
                            {{ $t('booking.tableFields.arrivalDate') }}
                        </th>
                        <th :class="{'booking-list__table-header--selected' : predicate[0] === 'departureDate'}"
                            class="booking-list__table-header"
                            @click="selectMultiPredicate('departureDate')">
                            {{ $t('booking.tableFields.departureDate') }}
                        </th>
                        <th :class="{'booking-list__table-header--selected' : predicate[0] === 'domain'}"
                            class="booking-list__table-header"
                            @click="selectMultiPredicate('domain')">
                            {{ $t('booking.tableFields.domain') }}
                        </th>
                        <th :class="{'booking-list__table-header--selected' : predicate[0] === 'accommodation'}"
                            class="booking-list__table-header"
                            @click="selectMultiPredicate('accommodation')">
                            {{ $t('booking.tableFields.accommodation') }}
                        </th>
                        <th :class="{'booking-list__table-header--selected' : predicate[0] === 'siteName'}"
                            class="booking-list__table-header"
                            @click="selectMultiPredicate('siteName')">
                            {{ $t('booking.tableFields.siteName') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(booking, index) in filteredBookingsPaginated"
                        :key="'booking-list-filtered-' + index">
                        <td class="booking-list__table-data">
                            {{ booking.supplierRef | noReference }}
                        </td>
                        <td class="booking-list__table-data">
                            {{ booking.bookingRef }}
                        </td>
                        <td class="booking-list__table-data">
                            {{ booking.customerName }}
                        </td>
                        <td class="booking-list__table-data">
                            {{ $t('bookingFilters.status.types.' + booking.status) }}
                        </td>
                        <td class="booking-list__table-data">
                            {{ booking.bookingDate | moment("DD/MM/YYYY") }}
                        </td>
                        <td class="booking-list__table-data">
                            {{ booking.arrivalDate | moment("DD/MM/YYYY") }}
                        </td>
                        <td class="booking-list__table-data">
                            {{ booking.departureDate | moment("DD/MM/YYYY") }}
                        </td>
                        <td class="booking-list__table-data">
                            {{ booking.domain }}
                        </td>
                        <td class="booking-list__table-data">
                            {{ booking.accommodation }}
                        </td>
                        <td class="booking-list__table-data">
                            {{ booking.siteName }}
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="booking-list__items">
                <div class="bookings-list__items-list">
                    <booking-item v-for="(booking, index) in filteredBookingsPaginated"
                                  :key="'booking-list-items-' + index"
                                  :supplier-ref="booking.supplierRef"
                                  :customer-name="booking.customerName"
                                  :booking-ref="booking.bookingRef"
                                  :booking-status="booking.status"
                                  :booking-date="booking.bookingDate"
                                  :arrival-date="booking.arrivalDate"
                                  :departure-date="booking.departureDate"
                                  :domain="booking.domain"
                                  :accommodation="booking.accommodation"
                                  :site="booking.siteName"/>
                </div>
            </div>
            <div v-if="error" :key="'error'" class="booking-list__error">
                <i class="fa fa-exclamation-circle"/>
                <div class="booking-list__error-message">{{ $t('bookingList.error') }}</div>
            </div>
            <div v-if="!error && (noContent || !filteredBookingsPaginated.length)"
                 :key="'no-content'"
                 class="booking-list__error">
                <i class="fa fa-exclamation-circle"/>
                <div class="booking-list__error-message">{{ $t('bookingList.noContent') }}</div>
            </div>
            <div class="booking-list__footer">
                <div class="booking-list__pagination">
                    <pagination-control :max-pages="filteredBookingsMaxPages"
                                        :current-page="(filteredBookingsMaxPages) === 0 ? 0 : page"
                                        @prev="decrementBookingPagination"
                                        @next="incrementBookingPagination"/>
                </div>
            </div>
        </div>
        <fake v-if="loading"
              :header-rows="1"
              :body-rows="10"
              :body-cells="4"
              :key="'fake'"/>
    </div>
</template>



// WEBPACK FOOTER //
// src/components/BookingList/BookingList.vue